import React, { useEffect, useState } from 'react';
import { Alert, Button, Collapse, Divider, Drawer, Form, Input, Select, Typography, message } from 'antd';
import { useMutation } from '@apollo/client';
import { showError } from 'utils/exceptionUtils';
import {
  CREATE_LOADING_POLICY,
  LoadingPolicyCatalog,
  LoadingPolicyIssue,
  LoadingPolicyItem,
  LoadingPolicySaveResult,
  LoadingPolicyValidation,
  UPDATE_LOADING_POLICY,
  VALIDATE_LOADING_POLICY,
} from '../graphql/loadingPolicyGraphql';
import {
  buildPolicyInput,
  CheckFormItem,
  checkIndexOfPath,
  defaultCheckItems,
  policyToBasicValues,
  policyToCheckItems,
  PolicyBasicValues,
} from '../loadingPolicyForm';
import CheckListEditor from './CheckListEditor';

type LoadingPolicyEditDrawerProps = {
  open: boolean;
  catalog: LoadingPolicyCatalog;
  /** null 为新增 */
  policy: LoadingPolicyItem | null;
  onClose: () => void;
  onSaved: () => void;
};

const FIELD_LABELS: Record<string, string> = {
  name: '名称',
  loadingUnit: '扫描对象',
  destinationWaypointTypes: '目的地类型',
  routeTypes: '路线类型',
  businessTypes: '业务来源',
  taskTypes: '任务类型',
  addressing: '目的地解析方式',
  checks: '校验',
  sideEffects: '附带动作',
  match: '与其他策略',
  version: '版本',
  id: '策略',
};

const pathLabel = (path: string): string => {
  const index = checkIndexOfPath(path);
  if (index !== null) return `校验第 ${index + 1} 项`;
  const base = path.replace(/\[\d+\]$/, '');
  return FIELD_LABELS[base] ?? path;
};

const IssueList: React.FC<{ type: 'error' | 'warning'; issues: LoadingPolicyIssue[] }> = ({ type, issues }) =>
  issues.length ? (
    <Alert
      type={type}
      showIcon
      message={type === 'error' ? `校验不通过（${issues.length}）` : `提示（${issues.length}）`}
      description={
        <ul className="m-0 pl-4">
          {issues.map((i) => (
            <li key={`${i.path}-${i.message}`}>
              <b>{pathLabel(i.path)}</b>：{i.message}
            </li>
          ))}
        </ul>
      }
    />
  ) : null;

const LoadingPolicyEditDrawer: React.FC<LoadingPolicyEditDrawerProps> = ({ open, catalog, policy, onClose, onSaved }) => {
  const [form] = Form.useForm<PolicyBasicValues>();
  const [checks, setChecks] = useState<CheckFormItem[]>([]);
  const [validation, setValidation] = useState<LoadingPolicyValidation | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const isEdit = policy !== null;

  const [validatePolicy] = useMutation<{ validateLoadingPolicy: LoadingPolicyValidation }>(VALIDATE_LOADING_POLICY);
  const [createPolicy] = useMutation<{ createLoadingPolicy: LoadingPolicySaveResult }>(CREATE_LOADING_POLICY);
  const [updatePolicy] = useMutation<{ updateLoadingPolicy: LoadingPolicySaveResult }>(UPDATE_LOADING_POLICY);

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    setValidation(null);
    if (policy) {
      form.setFieldsValue(policyToBasicValues(policy));
      setChecks(policyToCheckItems(policy));
    } else {
      form.setFieldsValue({ routeTypes: [], businessTypes: [], taskTypes: [], sideEffects: [], destinationWaypointTypes: [] });
      setChecks(defaultCheckItems(catalog));
    }
  }, [open, policy, catalog, form]);

  const currentInput = () => buildPolicyInput(catalog, form.getFieldsValue(true), checks);

  const handleValidate = async () => {
    try {
      const { data } = await validatePolicy({ variables: { input: currentInput(), id: policy?.id ?? null } });
      const result = data!.validateLoadingPolicy;
      setValidation(result);
      if (result.valid) message.success('校验通过');
    } catch (e: any) {
      showError(e);
    }
  };

  const handleSave = async () => {
    setSubmitting(true);
    try {
      const input = currentInput();
      const result = isEdit
        ? (await updatePolicy({ variables: { id: policy!.id, version: policy!.version, input } })).data!.updateLoadingPolicy
        : (await createPolicy({ variables: { input } })).data!.createLoadingPolicy;
      setValidation(result.validation);
      if (!result.policy) {
        message.error('未保存：请按提示修正后重试');
        return;
      }
      message.success('已保存：本实例立即生效，其他实例最长 5 分钟内生效');
      onSaved();
    } catch (e: any) {
      showError(e);
    } finally {
      setSubmitting(false);
    }
  };

  const fieldError = (name: string) => validation?.errors.filter((e) => e.path.replace(/\[\d+\]$/, '') === name).map((e) => e.message);
  const help = (name: string) => {
    const errors = fieldError(name);
    return errors?.length ? { validateStatus: 'error' as const, help: errors.join('；') } : {};
  };
  const options = (list: { value: string; label: string }[]) => list.map((o) => ({ value: o.value, label: o.label }));

  return (
    <Drawer
      open={open}
      width={860}
      onClose={onClose}
      destroyOnClose
      title={isEdit ? `编辑装车策略 #${policy!.id}（版本 ${policy!.version}）` : '新增装车策略'}
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>取消</Button>
          <Button onClick={handleValidate}>校验</Button>
          <Button type="primary" loading={submitting} onClick={handleSave}>
            保存
          </Button>
        </div>
      }
    >
      {policy && !policy.valid && (
        <Alert
          className="mb-4"
          type="error"
          showIcon
          message="该策略当前配置无法被装车流程加载，运行时会被跳过"
          description={policy.parseError}
        />
      )}
      <div className="mb-4 flex flex-col gap-2">
        <IssueList type="error" issues={validation?.errors ?? []} />
        <IssueList type="warning" issues={validation?.warnings ?? []} />
      </div>

      <Form form={form} layout="horizontal" labelCol={{ span: 5 }} wrapperCol={{ span: 19 }}>
        <Divider orientation="left">选策略</Divider>
        <Form.Item name="name" label="名称" required {...help('name')}>
          <Input placeholder="如 [按站点] 自有仓-干线；前缀标明匹配方式" maxLength={128} />
        </Form.Item>
        <Form.Item name="loadingUnit" label="扫描对象" required {...help('loadingUnit')}>
          <Select options={options(catalog.loadingUnits)} placeholder="请选择" />
        </Form.Item>
        <Form.Item name="routeTypes" label="路线类型" extra="不选为不限；交接策略只能选「交接」" {...help('routeTypes')}>
          <Select mode="multiple" allowClear options={options(catalog.routeTypes)} placeholder="不限" />
        </Form.Item>
        <Form.Item name="businessTypes" label="业务来源" extra="只有交接策略配置：交接任务按任务业务来源直接选中本策略" {...help('businessTypes')}>
          <Select mode="multiple" allowClear options={options(catalog.businessTypes)} placeholder="非交接策略不填" />
        </Form.Item>
        <Form.Item name="taskTypes" label="任务类型" extra="交接策略可选，用于区分 TikTok 三方 / 非三方；不选为不限" {...help('taskTypes')}>
          <Select mode="multiple" allowClear options={options(catalog.taskTypes)} placeholder="不限" />
        </Form.Item>
        <Form.Item name="destinationWaypointTypes" label="目的地类型" required {...help('destinationWaypointTypes')}>
          <Select mode="multiple" options={options(catalog.destinationWaypointTypes)} placeholder="请选择" />
        </Form.Item>
        <Form.Item name="addressing" label="目的地解析方式" required {...help('addressing')}>
          <Select options={options(catalog.addressingModes)} placeholder="请选择" />
        </Form.Item>
        {(fieldError('match') ?? []).length > 0 && (
          <Alert className="mb-4" type="error" showIcon message={(fieldError('match') ?? []).join('；')} />
        )}

        <Divider orientation="left">校验</Divider>
        <Typography.Paragraph type="secondary" className="text-xs">
          固定执行、不可配置：{catalog.fixedChecks.map((c) => c.label).join('；')}
        </Typography.Paragraph>
        <CheckListEditor catalog={catalog} value={checks} onChange={setChecks} errors={validation?.errors ?? []} />

        <Divider orientation="left">附带动作</Divider>
        <Form.Item name="sideEffects" label="附带动作" {...help('sideEffects')}>
          <Select mode="multiple" allowClear options={options(catalog.sideEffects)} placeholder="无" />
        </Form.Item>
      </Form>

      {validation?.valid && (
        <Collapse
          className="mt-4"
          items={[
            {
              key: 'json',
              label: '将写入的配置（JSON 预览）',
              children: (
                <pre className="m-0 whitespace-pre-wrap break-all text-xs">
                  {`checks_json:\n${validation.checksJson ?? 'NULL'}\n\nside_effects_json:\n${validation.sideEffectsJson ?? 'NULL'}`}
                </pre>
              ),
            },
          ]}
        />
      )}
    </Drawer>
  );
};

export default LoadingPolicyEditDrawer;
